import React, { Component, useState } from 'react';
import { bool, func, object, shape, string } from 'prop-types';
import { Field } from 'react-final-form';
import classNames from 'classnames';
import { ValidationError, ExpandingTextarea, Button } from '../../components';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import css from './FieldTextInput.module.css';
import moment from 'moment';
import IconCollection from '../IconCollection/IconCollection';

const CONTENT_MAX_LENGTH = 5000;

const FieldTextInputComponent = props => {
  const {
    rootClassName,
    className,
    inputRootClass,
    customErrorText,
    id,
    label,
    input,
    meta,
    onUnmount,
    isUncontrolled,
    inputRef,
    hideErrorMessage,
    showPassword,
    handleTogglePasswordVisibility,
    showPass,
    placeholder,
    ...rest
  } = props;
  // if (label && !id) {
  //   throw new Error('id required when a label is given');
  // }

  const { valid, invalid, touched, error } = meta;
  const isTextarea = input.type === 'textarea';

  const errorText = customErrorText || error;

  // Error message and input error styles are only shown if the
  // field has been touched and the validation has failed.
  const hasError = !!customErrorText || !!(touched && invalid && error);

  const fieldMeta = { touched: hasError, error: errorText };

  // Textarea doesn't need type.
  const { type, ...inputWithoutType } = input;
  // Uncontrolled input uses defaultValue instead of value.
  const { value: defaultValue, ...inputWithoutValue } = input;
  // Use inputRef if it is passed as prop.
  const refMaybe = inputRef ? { ref: inputRef } : {};

  const inputClasses =
    inputRootClass ||
    classNames(css.input, {
      [css.inputSuccess]: valid,
      [css.inputError]: hasError,
      [css.textarea]: isTextarea,
    });
  const maxLength = CONTENT_MAX_LENGTH;
  const inputProps = isTextarea
    ? {
        className: inputClasses,
        id,
        rows: 1,
        maxLength,
        placeholder, 
        ...refMaybe,
        ...inputWithoutType,
        ...rest,
      }
    : isUncontrolled
    ? {
        className: inputClasses,
        id,
        type,
        defaultValue,
        placeholder, 
        ...refMaybe,
        ...inputWithoutValue,
        ...rest,
      }
    : { 
        className: inputClasses, 
        id, 
        type, 
        placeholder, 
        ...refMaybe, 
        ...input, 
        ...rest 
      };

    const arabicLanguage = typeof window !== 'undefined' && localStorage.getItem("language") === "Arabic";

  const classes = classNames(rootClassName || css.root, className, arabicLanguage && css.arabicDirection);

  if (input.type === 'date') {
    Object.assign(inputProps, {
      max: moment()
        .subtract(1, 'days')
        .format('YYYY-MM-DD'),
      placeholder: 'mm/dd/yy', // Add placeholder for date input
      onClick: (e) => e.target.showPicker() 
    });
  }
  

  return (
    <div className={classes}>
      {label ? <label >{label}</label> : null}
      {isTextarea ? (
        <ExpandingTextarea {...inputProps} />
      ) : (
        <div className={css.inputWrapper}>
          <input
            type={input.type}
            {...inputProps}
          />
          {/* {input.type === 'date' && (
            <span className={css.calendarIcon} ><IconCollection name="input_calender"/></span>
          )} */}
          {showPass && (
            <Button
              type="button"
              className={css.togglePassword}
              onClick={handleTogglePasswordVisibility}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? (
                <FormattedMessage id="SignupForm.hideText" />
              ) : (
                <FormattedMessage id="SignupForm.showText" />
              )}
            </Button>
          )}
        </div>
      )}
      {hideErrorMessage ? null : <ValidationError fieldMeta={fieldMeta} />}
    </div>
  );
};

FieldTextInputComponent.defaultProps = {
  rootClassName: null,
  className: null,
  inputRootClass: null,
  onUnmount: null,
  customErrorText: null,
  id: null,
  label: null,
  isUncontrolled: false,
  inputRef: null,
};

FieldTextInputComponent.propTypes = {
  rootClassName: string,
  className: string,
  inputRootClass: string,
  onUnmount: func,

  // Error message that can be manually passed to input field,
  // overrides default validation message
  customErrorText: string,

  // Label is optional, but if it is given, an id is also required so
  // the label can reference the input in the `for` attribute
  id: string,
  label: string,

  // Uncontrolled input uses defaultValue prop, but doesn't pass value from form to the field.
  // https://reactjs.org/docs/uncontrolled-components.html#default-values
  isUncontrolled: bool,
  // a ref object passed for input element.
  inputRef: object,

  // Generated by final-form's Field component
  input: shape({
    onChange: func.isRequired,
    // Either 'textarea' or something that is passed to the input element
    type: string.isRequired,
  }).isRequired,
  meta: object.isRequired,
};

class FieldTextInput extends Component {
  componentWillUnmount() {
    // Unmounting happens too late if it is done inside Field component
    // (Then Form has already registered its (new) fields and
    // changing the value without corresponding field is prohibited in Final Form
    if (this.props.onUnmount) {
      this.props.onUnmount();
    }
  }

  render() {
    return <Field component={FieldTextInputComponent} {...this.props} />;
  }
}

export default FieldTextInput;
